"use client";

import { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { saveTierList, saveImage, setLastUsedTierListId } from "@/lib/indexdb";
import { TierList, Image } from "@/types/tier-list";
import { base64ToBlob } from "@/lib/template-utils";
import { ColorConfigModal } from "@/components/color-config-modal";
import { loadTemplate, getTemplateMetadata } from "@/lib/templates-index";
import { TiersSection } from "@/components/tiers-section";
import { LogoUpload, LogoUploadRef } from "@/components/logo-upload";
import { useTierManagement } from "@/hooks/use-tier-management";

const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;

const tierSchema = z.object({
  id: z.string(),
  label: z.string().min(1, "Label is required"),
  labelColor: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (e.g., #FF0000)"),
  backgroundColor: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (e.g., #FFCCCC)"),
});

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formSchema = z.object({
  name: z.string().min(1, "Tier list name is required"),
  logo: z
    .any()
    .optional()
    .refine(
      (files) =>
        !files || files?.length === 0 || files?.[0]?.type?.startsWith("image/"),
      "Logo must be an image file"
    )
    .refine(
      (files) =>
        !files || files?.length === 0 || files?.[0]?.size <= MAX_FILE_SIZE,
      "Logo must be under 10MB"
    ),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTierListFormProps {
  onSuccess: (tierListId: string) => void;
  startFromScratch?: boolean; // If true, start with no default tiers
  templateId?: string | null; // If provided, pre-fill from this template
}

export function CreateTierListForm({
  onSuccess,
  startFromScratch = false,
  templateId = null,
}: CreateTierListFormProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFromTemplate, setLogoFromTemplate] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string>("");
  const [tiersError, setTiersError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logoUploadRef = useRef<LogoUploadRef>(null);

  // Initialize tiers based on mode
  const getInitialTiers = () => {
    // If starting from scratch, start with empty array
    if (startFromScratch) {
      return [];
    }

    // If template is provided, use template metadata tiers
    if (templateId) {
      const metadata = getTemplateMetadata(templateId);
      if (metadata) {
        return metadata.tiers.map((tier) => ({
          id: tier.id,
          label: tier.label,
          labelColor: tier.color.label,
          backgroundColor: tier.color.background,
        }));
      }
    }

    // Default tiers (for backward compatibility when called without props)
    return [
      {
        id: uuidv4(),
        label: "S+",
        labelColor: "#000000",
        backgroundColor: "#FCD34D",
      },
      {
        id: uuidv4(),
        label: "S",
        labelColor: "#FFFFFF",
        backgroundColor: "#7C3AED",
      },
      {
        id: uuidv4(),
        label: "A",
        labelColor: "#FFFFFF",
        backgroundColor: "#2563EB",
      },
      {
        id: uuidv4(),
        label: "B",
        labelColor: "#FFFFFF",
        backgroundColor: "#16A34A",
      },
      {
        id: uuidv4(),
        label: "C",
        labelColor: "#FFFFFF",
        backgroundColor: "#EA580C",
      },
      {
        id: uuidv4(),
        label: "D",
        labelColor: "#FFFFFF",
        backgroundColor: "#DC2626",
      },
      {
        id: uuidv4(),
        label: "F",
        labelColor: "#FFFFFF",
        backgroundColor: "#64748B",
      },
    ];
  };

  const tierManagement = useTierManagement(getInitialTiers());

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  // Load full template if templateId is provided (for name and logo)
  useEffect(() => {
    if (templateId) {
      loadTemplate(templateId)
        .then((template) => {
          // Set form name
          form.setValue("name", template.name);

          // Set logo if present
          if (template.logo) {
            setLogoFromTemplate(template.logo);
          }
        })
        .catch((error) => {
          console.error("Failed to load template:", error);
        });
    }
  }, [templateId, form]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setLogoError("");
    setTiersError("");
    setIsSubmitting(true);

    try {
      // Validate tiers
      if (tierManagement.localTiers.length === 0) {
        setTiersError(
          "At least one tier is required. Click 'Add Tier' to get started."
        );
        setIsSubmitting(false);
        return;
      }

      // Validate localTiers manually
      const hasEmptyLabel = tierManagement.localTiers.some((tier) => !tier.label.trim());
      if (hasEmptyLabel) {
        setTiersError(
          "All tiers must have a label. Please fill in all tier labels."
        );
        setIsSubmitting(false);
        return;
      }

      // Check if we have a logo (either from upload or template)
      const values = form.getValues();
      const hasLogoFile = values.logo && values.logo.length > 0;
      const hasLogoFromTemplate = logoFromTemplate !== null;

      if (!hasLogoFile && !hasLogoFromTemplate) {
        setLogoError("Logo is required. Please upload a logo.");
        setIsSubmitting(false);
        return;
      }

      // Trigger form validation (for name field)
      const isValid = await form.trigger();
      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      const tierListId = uuidv4();
      let logoId: string | undefined;

      // Save logo image (either from file or template)
      if (hasLogoFile) {
        const logoFile = values.logo[0];
        logoId = uuidv4();
        const image: Image = {
          id: logoId,
          tierListId,
          blob: logoFile,
          mimeType: logoFile.type,
        };
        await saveImage(image);
      } else if (hasLogoFromTemplate) {
        // Convert base64 to blob
        logoId = uuidv4();
        const blob = base64ToBlob(logoFromTemplate);
        const image: Image = {
          id: logoId,
          tierListId,
          blob,
          mimeType: blob.type,
        };
        await saveImage(image);
      }

      // Create tier list using localTiers
      const tierList: TierList = {
        id: tierListId,
        name: values.name,
        tiers: tierManagement.localTiers.map((tier, index) => ({
          id: uuidv4(),
          label: tier.label,
          priority: index,
          color: {
            label: tier.labelColor as `#${string}`,
            background: tier.backgroundColor as `#${string}`,
          },
        })),
        logoId,
        version: 1, // Set initial version
      };

      await saveTierList(tierList);
      await setLastUsedTierListId(tierListId);

      onSuccess(tierListId);
    } catch (error) {
      console.error("Failed to create tier list:", error);
      setTiersError("Failed to create tier list. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      form.setValue("logo", files);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
        // Clear template logo when user uploads new one
        setLogoFromTemplate(null);
        // Clear logo error when user uploads
        setLogoError("");
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleLogoRemove = () => {
    setLogoPreview(null);
    setLogoFromTemplate(null);
    form.setValue("logo", null);
    logoUploadRef.current?.clearInput();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Tier List Name</Label>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="name"
                  placeholder="My Awesome Tier List"
                  {...field}
                />
                {fieldState.error && (
                  <p className="text-sm text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="logo">Logo</Label>
          <Controller
            control={form.control}
            name="logo"
            render={({ fieldState }) => (
              <LogoUpload
                ref={logoUploadRef}
                logoPreview={logoPreview || logoFromTemplate}
                onLogoChange={handleLogoChange}
                onLogoRemove={handleLogoRemove}
                error={fieldState.error?.message || logoError}
              />
            )}
          />
        </div>

        {tiersError && (
          <p className="text-sm text-destructive">{tiersError}</p>
        )}

        <TiersSection
          tiers={tierManagement.localTiers}
          onLabelChange={tierManagement.handleLabelChange}
          openColorModal={tierManagement.openColorModal}
          removeTier={tierManagement.removeTier}
          addTier={tierManagement.addTier}
          onDragStart={tierManagement.handleDragStart}
          onDragEnd={tierManagement.handleDragEnd}
          activeId={tierManagement.activeId}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Tier List"}
        </Button>
      </form>
      <ColorConfigModal
        isOpen={tierManagement.colorModalOpen}
        onClose={tierManagement.closeColorModal}
        initialLabelColor={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex]
                .labelColor
            : "#000000"
        }
        initialBackgroundColor={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex]
                .backgroundColor
            : "#CCCCCC"
        }
        onSave={tierManagement.saveColors}
        tierLabel={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex].label ||
              `${tierManagement.editingTierIndex + 1}`
            : ""
        }
      />
    </>
  );
}
